import type { AppRouter } from "@repo/api";
import type { inferRouterOutputs } from "@trpc/server";

import type { ParticipantSubmissionStatus } from "@/lib/opportunity-utils";

type ServerOnboardingData =
  inferRouterOutputs<AppRouter>["onboarding"]["getData"];

export type ApplicationStepKey = "CONNECT_PROFILES" | "ID_VERIFICATION";

interface ApplicationStep {
  key: ApplicationStepKey;
  label: string;
  shortLabel: string;
  required: boolean;
}

export const APPLICATION_STEPS: ApplicationStep[] = [
  {
    key: "CONNECT_PROFILES",
    label: "Connect your profiles",
    shortLabel: "Profiles",
    required: false,
  },
  {
    key: "ID_VERIFICATION",
    label: "ID verification",
    shortLabel: "ID",
    required: true,
  },
];

export function computeCompletedRequirements(
  data: ServerOnboardingData,
): Set<ApplicationStepKey> {
  const completed = new Set<ApplicationStepKey>();
  const { user, accounts } = data;

  // CONNECT_PROFILES is optional (required: false), so this only affects
  // whether the checkmark appears on the /me/opportunities page; it never
  // blocks interview access.
  if (accounts.some((a) => ["linkedin", "facebook"].includes(a.providerId))) {
    completed.add("CONNECT_PROFILES");
  }

  // ID_VERIFICATION
  if (user.verificationStatus === "VERIFIED") {
    completed.add("ID_VERIFICATION");
  }

  return completed;
}

export type InterviewStepStatus =
  | "NOT_STARTED"
  | "PROCESSING"
  | "AWAITING_REVIEW"
  | "COMPLETED"
  | "SCREENED_OUT"
  | "NOT_SELECTED"
  | "EXPIRED";

/**
 * Where the applicant stands on the interview step of the apply checklist,
 * read from the participation status the page already holds.
 *
 * This used to come from `opportunityListing.getMyInterviewStatusForStudy`,
 * which returned the latest non-SCREENING `Interview` row for the pair. No such
 * row has been written since 2026-07-23, and 107,411 of the 125,218 live
 * submissions have none at all, so the step could never tick and the progress
 * counter was permanently one short for every applicant.
 *
 * `useParticipationStatuses` already unions the interview-derived badges with
 * `SubmissionService.getSubmissionStatusesForUser` and lets the submission win,
 * so the answer was two lines above the broken call on both pages.
 *
 * `COMPLETED` is the checklist's *approved* state: it renders as "Approved"
 * with a blue tick. A rejection and a timeout are terminal too, but they are
 * not approvals, so they get their own states rather than borrowing that one.
 */
export function getInterviewStepStatus(
  status: ParticipantSubmissionStatus | null | undefined,
): InterviewStepStatus {
  switch (status) {
    case undefined:
    case null:
      return "NOT_STARTED";
    case "IN_PROGRESS":
    case "PROCESSING":
      return "PROCESSING";
    case "AWAITING_REVIEW":
      return "AWAITING_REVIEW";
    case "SCREENED_OUT":
      return "SCREENED_OUT";
    case "NOT_SELECTED":
      return "NOT_SELECTED";
    case "EXPIRED":
      return "EXPIRED";
    case "APPROVED":
    case "PAID":
      return "COMPLETED";
  }
}

/**
 * Whether the interview step counts towards the checklist's progress counter.
 *
 * "Done" here means closed, not successful: the applicant has nothing left to
 * do on this opportunity. A rejection, a screen-out and a timeout all close the
 * step, and leaving it open would ask them to repeat work they cannot repeat.
 * Only an attempt still in flight, or none at all, leaves it open.
 *
 * Both opportunity detail pages read this. It lives here so a new terminal
 * status cannot be added to the mapping above and forgotten in one of them.
 */
export function isInterviewStepDone(status: InterviewStepStatus): boolean {
  switch (status) {
    case "COMPLETED":
    case "AWAITING_REVIEW":
    case "SCREENED_OUT":
    case "NOT_SELECTED":
    case "EXPIRED":
      return true;
    case "PROCESSING":
    case "NOT_STARTED":
      return false;
  }
}
